/**
 * Wire enum for the shipments aggregate. Mirrors the BE `ShipmentStatus`
 * JsonStringEnumConverter values exactly.
 */
export type ShipmentStatus = 'Packed' | 'Shipped';

const KNOWN_STATUSES: readonly ShipmentStatus[] = ['Packed', 'Shipped'];

/**
 * Normalize a possibly-untyped status payload (string from JSON, legacy numeric, null/undefined)
 * to a known `ShipmentStatus`. Defaults to `'Packed'` for unknown inputs so per-row badges and
 * mutation guards never blow up if the backend ever stops sending the field.
 */
export function normalizeShipmentStatus(
  status: ShipmentStatus | string | number | null | undefined,
): ShipmentStatus {
  if (typeof status === 'string') {
    const normalized = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    const match = KNOWN_STATUSES.find((candidate) => candidate === normalized);
    return match ?? 'Packed';
  }
  if (typeof status === 'number') {
    // Backend numeric ordering: Packed = 0, Shipped = 1.
    return status === 1 ? 'Shipped' : 'Packed';
  }
  return 'Packed';
}

/**
 * Maps a wire status onto its i18n label key. Caller is responsible for translating.
 *
 * Both the standalone shipments page and the sales-order shipments tab use the same
 * `COMPONENTS.SHIPMENTS.STATUSES.*` subtree so the visible text stays consistent across
 * surfaces. Accepts loose payloads so consumers that read the field straight off a JSON
 * envelope do not have to normalize themselves.
 */
export function getShipmentStatusLabelKey(
  status: ShipmentStatus | string | number | null | undefined,
): string {
  const normalized = normalizeShipmentStatus(status);
  return `COMPONENTS.SHIPMENTS.STATUSES.${normalized.toUpperCase()}`;
}

/**
 * Maps a wire status onto a coarse severity badge class so the list and detail pages
 * can colour the badge consistently without each consumer re-deriving the rule.
 */
export function getShipmentStatusBadgeKey(status: ShipmentStatus): 'warning' | 'success' {
  return status === 'Shipped' ? 'success' : 'warning';
}
